import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Subscription } from '../subscriptions/core/_models';
import { getSubscriptionByToken, checkoutSubscription, validateCouponPublic } from '../subscriptions/core/subscriptionService';
import { useToast } from '../components/Toats';
import { useThemeMode } from '../../../_metronic/partials';
import { WhiteLabelProvider } from '../subscriptions/components/WhiteLabelProvider';
import { getFileBase64Public } from '../../services/files';
import { toAbsoluteUrl } from '../../../_metronic/helpers';

export const CheckoutPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toggleToast } = useToast();
  const { mode } = useThemeMode();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [logoSrc, setLogoSrc] = useState<string>('');
  const [logoLoading, setLogoLoading] = useState<boolean>(true);
  const [paymentMethod, setPaymentMethod] = useState<'BOLETO' | 'CARTAO'>('BOLETO');

  useEffect(() => {
    if (token) {
      getSubscriptionByToken(token).then((data: any) => {
        setSubscription(data);
        if (data.subscriptionItems) {
          setItems(data.subscriptionItems.map((item: any) => ({
            ...item,
            is_included: item.is_required ? true : (item.is_included ?? true)
          })));
        }
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    }
  }, [token]);

  useEffect(() => {
    const company = subscription?.unitBranch?.company;
    if (company) {
      const imgKey = mode === 'dark' && company.image_dark ? company.image_dark : company.image;
      if (imgKey) {
        if (imgKey.includes('/') || imgKey.startsWith('data:')) {
          setLogoSrc(imgKey);
          setLogoLoading(false);
        } else {
          setLogoLoading(true);
          getFileBase64Public(imgKey).then((res: any) => {
            if (res && res.base64) {
              setLogoSrc(`data:image/png;base64,${res.base64}`);
            } else {
              setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
            }
            setLogoLoading(false);
          }).catch(() => {
            setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
            setLogoLoading(false);
          });
        }
      } else {
        setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
        setLogoLoading(false);
      }
    } else {
      if (subscription) {
        setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
        setLogoLoading(false);
      }
    }
  }, [subscription, mode]);

  const handleSubscribe = async () => {
    if (!token) return;

    if (paymentMethod === 'CARTAO') {
      navigate(`/checkout/${token}/pagamento`);
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        coupon_code: appliedCoupon ? appliedCoupon.code : couponCode,
        items: items.map(i => ({ id: i.id, is_included: i.is_included })),
        payment_method: paymentMethod,
      };

      await checkoutSubscription(token, payload);
      setSubmitting(false);
      navigate(`/checkout/${token}/success`);
    } catch (err: any) {
      setSubmitting(false);
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Erro ao processar assinatura';
      toggleToast(msg, 'error');
    }
  };

  const handleValidateCoupon = () => {
    if (!token || !couponCode.trim()) return;
    setValidatingCoupon(true);
    setCouponError('');
    validateCouponPublic(token, couponCode).then((data) => {
      setAppliedCoupon(data);
      setValidatingCoupon(false);
    }).catch((err) => {
      setAppliedCoupon(null);
      setValidatingCoupon(false);
      setCouponError(err?.response?.data?.error || 'Cupom inválido');
    });
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponError('');
  };

  if (loading) {
    return (
      <div className='d-flex flex-column flex-root bg-body h-100' style={{ minHeight: '100vh' }}>
        <div className='d-flex flex-column flex-center w-100 p-10'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className='d-flex flex-column flex-root bg-body h-100' style={{ minHeight: '100vh' }}>
        <div className='d-flex flex-column flex-center w-100 p-10'>
          <h1 className='text-gray-800 fw-bolder mb-3'>Link Inválido ou Expirado</h1>
          <div className='text-muted fw-bold fs-5'>Verifique o link recebido e tente novamente.</div>
        </div>
      </div>
    );
  }

  const cycleMultipliers: Record<string, number> = {
    MENSAL: 1,
    TRIMESTRAL: 3,
    SEMESTRAL: 6,
    ANUAL: 12,
  };

  const calculateRecurringMonthly = () => {
    let monthly = Number(subscription.monthly_value) || 0;
    items.forEach(item => {
      if (!item.is_included) {
        monthly -= Number(item.total_price) || 0;
      }
    });
    return monthly;
  };

  const calculateTotalToday = () => {
    const totalInstallments = Number(subscription.total_installments) || 0;
    let baseTotal: number;

    if (totalInstallments > 1) {
      baseTotal = calculateRecurringMonthly() + (Number(subscription.adhesion_fee) || 0);
    } else {
      const mult = cycleMultipliers[subscription.cycle] || 1;
      baseTotal = (calculateRecurringMonthly() * mult) + (Number(subscription.adhesion_fee) || 0);
    }

    if (appliedCoupon) {
      if (appliedCoupon.discount_type === 'PERCENTAGE') {
        baseTotal -= baseTotal * (appliedCoupon.discount_value / 100);
      } else {
        baseTotal = Math.max(0, baseTotal - appliedCoupon.discount_value);
      }
    }

    return baseTotal;
  };

  const calculateDiscountAmount = () => {
    const mult = cycleMultipliers[subscription.cycle] || 1;
    let baseTotal = (calculateRecurringMonthly() * mult) + (Number(subscription.adhesion_fee) || 0);

    if (appliedCoupon) {
      if (appliedCoupon.discount_type === 'PERCENTAGE') {
        return baseTotal * (appliedCoupon.discount_value / 100);
      } else {
        return Math.min(baseTotal, appliedCoupon.discount_value);
      }
    }
    return 0;
  };

  const company = subscription.unitBranch?.company;

  return (
    <WhiteLabelProvider company={company}>
      <div className='d-flex flex-column flex-root bg-body h-100' style={{ minHeight: '100vh' }}>
        <div className='d-flex flex-column flex-center w-100 p-10'>
          <div className='card card-flush shadow-sm mw-800px w-100'>
            <div className='card-header pt-8'>
              <div className='card-title flex-column'>
                <h2 className='mb-2 text-gray-800'>Finalizar Contratação</h2>
                <div className='text-muted fs-6 fw-bold'>Revise os itens e confirme sua assinatura</div>
              </div>
              <div className='card-toolbar'>
                {logoLoading ? (
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Carregando logo...</span>
                  </div>
                ) : (
                  logoSrc && <img alt='Logo' src={logoSrc} className='h-30px' />
                )}
              </div>
            </div>
            <div className='card-body pt-5'>
            <div className='row g-10'>
              {/* Coluna 1: Resumo do Plano */}
              <div className='col-lg-6'>
                <h3 className='fw-bolder text-gray-800 mb-5'>O que está incluso:</h3>
                <div className='d-flex align-items-center mb-5'>
                  <div className='symbol symbol-50px me-5'>
                    <span className='symbol-label bg-light-primary'>
                      <i className='bi bi-shield-check text-primary fs-2x'></i>
                    </span>
                  </div>
                  <div className='d-flex flex-column'>
                    <a className='text-gray-800 text-hover-primary fs-5 fw-bolder'>{subscription.plan?.name || 'Assinatura Personalizada'}</a>
                    <span className='text-muted fw-bold'>Revisão de itens para {subscription.client?.name || 'Cliente'}</span>
                  </div>
                </div>

                <div className='separator border-gray-200 mb-5'></div>

                <div className='d-flex flex-column mb-8'>
                  {((Number(subscription.monthly_value) || 0) - items.reduce((acc, i) => acc + (Number(i.total_price) || 0), 0) > 0) && (
                    <div className='d-flex align-items-center justify-content-between py-2'>
                      <div className='d-flex align-items-center'>
                        <span className='bullet bg-primary me-3'></span>
                        <div className='d-flex flex-column'>
                          <span className='text-gray-800 fw-bolder'>Valor Base do Plano</span>
                        </div>
                      </div>
                      <div className='text-gray-800 fw-bold'>
                        R$ {((Number(subscription.monthly_value) || 0) - items.reduce((acc, i) => acc + (Number(i.total_price) || 0), 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} / mês
                      </div>
                    </div>
                  )}

                  {items.map((item, idx) => {
                    const itemName = item.product?.name || item.product?.description || item.typeService?.description || item.typeService?.name || `Item ${idx + 1}`;
                    return (
                      <div key={item.id} className='d-flex align-items-center justify-content-between py-2'>
                        <div className='d-flex align-items-center'>
                          {item.is_required ? (
                            <span className='bullet bg-primary me-3'></span>
                          ) : (
                            <div className='form-check form-check-custom form-check-solid form-check-sm me-3'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={item.is_included}
                                onChange={(e) => {
                                  const newItems = [...items];
                                  newItems[idx].is_included = e.target.checked;
                                  setItems(newItems);
                                }}
                              />
                            </div>
                          )}
                          <div className='d-flex flex-column'>
                            <span className='text-gray-800 fw-bolder'>{itemName}</span>
                            {!item.is_required && <span className='text-muted fs-7'>Opcional</span>}
                          </div>
                        </div>
                        <div className='text-gray-800 fw-bold'>
                          R$ {Number(item.total_price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} / mês
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Coluna 2: Pagamento e Total */}
              <div className='col-lg-6'>
                <div className='bg-light rounded p-8'>
                  <h3 className='fw-bolder text-gray-800 mb-5'>Ciclo de Cobrança</h3>

                  <div className='d-flex flex-column mb-8 gap-3'>
                    <div className='bg-white border border-primary border-dashed rounded p-5 d-flex align-items-center justify-content-between'>
                      <div className='d-flex align-items-center'>
                        <i className='bi bi-calendar-check text-primary fs-2x me-4'></i>
                        <div className='d-flex flex-column'>
                          <span className='text-gray-800 fw-bolder fs-4'>
                            {subscription.cycle === 'MENSAL' && 'Mensal'}
                            {subscription.cycle === 'SEMESTRAL' && 'Semestral'}
                            {subscription.cycle === 'ANUAL' && 'Anual'}
                          </span>
                          <span className='text-muted fw-bold fs-7'>Ciclo de faturamento</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='mb-8'>
                    <label className='form-label fw-bold text-gray-800'>Possui um cupom de desconto?</label>
                    <div className='input-group'>
                      <input
                        type='text'
                        className='form-control border border-gray-400 bg-white'
                        placeholder='Digite seu cupom'
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value.toUpperCase());
                          if (couponError) setCouponError('');
                        }}
                        disabled={!!appliedCoupon}
                      />
                      {appliedCoupon ? (
                        <button className='btn btn-danger' type='button' onClick={handleRemoveCoupon}>
                          Remover
                        </button>
                      ) : (
                        <button className='btn btn-primary' type='button' onClick={handleValidateCoupon} disabled={!couponCode.trim() || validatingCoupon}>
                          {validatingCoupon ? <span className='spinner-border spinner-border-sm'></span> : 'Aplicar'}
                        </button>
                      )}
                    </div>
                    {couponError && <div className='text-danger fs-7 mt-2'>{couponError}</div>}
                    {appliedCoupon && (
                      <div className='text-success fs-7 mt-2 fw-bolder'>
                        Cupom de {appliedCoupon.discount_type === 'PERCENTAGE' ? `${appliedCoupon.discount_value}%` : `R$ ${appliedCoupon.discount_value.toFixed(2)}`} aplicado com sucesso! ({appliedCoupon.duration_mode === 'FIRST_ONLY' ? 'Apenas 1ª parcela' : 'Todas as parcelas'})
                      </div>
                    )}
                  </div>

                  <div className='separator border-gray-300 mb-5'></div>

                  <div className='d-flex flex-stack mb-2'>
                    <span className='fw-bold fs-6 text-gray-600'>
                      {(() => {
                        const totalInstallments = Number(subscription.total_installments) || 0;
                        if (totalInstallments > 1) {
                          return `${totalInstallments}x de R$ ${calculateRecurringMonthly().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
                        }
                        const cycleLabel = subscription.cycle === 'MENSAL' ? 'Mensal' : subscription.cycle === 'TRIMESTRAL' ? 'Trimestral' : subscription.cycle === 'SEMESTRAL' ? 'Semestral' : 'Anual';
                        return `Recorrente (${cycleLabel})`;
                      })()}
                    </span>
                    <span className='fw-bold fs-5 text-gray-800'>
                      {(() => {
                        const totalInstallments = Number(subscription.total_installments) || 0;
                        if (totalInstallments > 1) {
                          return `R$ ${calculateRecurringMonthly().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
                        }
                        return `R$ ${(calculateRecurringMonthly() * (cycleMultipliers[subscription.cycle] || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
                      })()}
                    </span>
                  </div>

                  {Number(subscription.adhesion_fee) > 0 && (
                    <div className='d-flex flex-stack mb-2'>
                      <span className='fw-bold fs-6 text-success'>Taxa de Adesão (Única)</span>
                      <span className='fw-bold fs-5 text-success'>
                        + R$ {Number(subscription.adhesion_fee).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}

                  {appliedCoupon && (
                    <div className='d-flex flex-stack mb-5'>
                      <span className='fw-bold fs-6 text-danger'>Desconto ({appliedCoupon.code})</span>
                      <span className='fw-bold fs-5 text-danger'>
                        - R$ {calculateDiscountAmount().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}

                  <div className='separator border-gray-300 border-dashed mb-5'></div>

                  <div className='d-flex flex-stack mb-5'>
                    <div className='d-flex flex-column'>
                      <span className='fw-bolder fs-4 text-gray-800'>Total a Pagar Hoje</span>
                      <span className='fs-7 text-muted'>1ª fatura</span>
                    </div>
                    <span className='fw-bolder fs-2x text-primary'>
                      R$ {calculateTotalToday().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  <button className='btn btn-primary w-100 fw-bolder fs-5' onClick={handleSubscribe} disabled={submitting}>
                    {submitting ? <span className='spinner-border spinner-border-sm align-middle ms-2'></span> : 'Assinar Agora'}
                  </button>
                  <div className='text-center mt-4'>
                    <span className='text-muted fs-7'><i className='bi bi-lock-fill text-muted me-1'></i> Ambiente 100% Seguro</span>
                  </div>
                </div>

                <div className='separator border-gray-200 my-5'></div>

                <h3 className='fw-bolder text-gray-800 mb-5'>Forma de Pagamento</h3>
                <div className='d-flex gap-3 mb-5'>
                  <div
                    className={`border rounded p-4 flex-fill cursor-pointer text-center ${paymentMethod === 'BOLETO' ? 'border-primary bg-light-primary' : 'border-gray-300'}`}
                    onClick={() => { setPaymentMethod('BOLETO'); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className={`bi bi-upc-scan fs-2x mb-2 ${paymentMethod === 'BOLETO' ? 'text-primary' : 'text-gray-500'}`}></i>
                    <div className={`fw-bolder ${paymentMethod === 'BOLETO' ? 'text-primary' : 'text-gray-700'}`}>Boleto / Pix</div>
                    <span className='text-muted fs-8'>Link de pagamento</span>
                  </div>
                  <div
                    className={`border rounded p-4 flex-fill cursor-pointer text-center ${paymentMethod === 'CARTAO' ? 'border-primary bg-light-primary' : 'border-gray-300'}`}
                    onClick={() => setPaymentMethod('CARTAO')}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className={`bi bi-credit-card fs-2x mb-2 ${paymentMethod === 'CARTAO' ? 'text-primary' : 'text-gray-500'}`}></i>
                    <div className={`fw-bolder ${paymentMethod === 'CARTAO' ? 'text-primary' : 'text-gray-700'}`}>Cartão de Crédito</div>
                    <span className='text-muted fs-8'>Cobrança automática</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </WhiteLabelProvider>
  );
};
